import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useCan } from '@/auth/consoleCapabilityContext';
import {
    deleteAccount,
    fetchAccountAudit,
    resetAccountPassword,
    toUnifiedFromAccountAudit,
    updateAccountEnabled,
    type AccountPasswordReset,
} from '@/api/console';
import { useResetCredential } from '../useResetCredential';
import { OrgAdminApiError, OrgAdminUnauthorizedError } from '@/api/orgAdmin';
import { useToast } from '@/components/ui';
import { fetchAccount } from '@/api/accounts';
import type { KeycloakAccountRow } from '../types';
import { accountDisplayName } from './accountDisplayName';
import type { AccountProfileTabKey } from './AccountProfileTabs';

/**
 * EAA-1820 (frame 72): data + write flows for `/console/accounts/:sub` —
 * detail fetch, tab state in `?tab=`, and the Reset / Lock / Delete dialogs
 * reused from the list screen.
 */
export function useAccountProfilePage() {
    const { t } = useTranslation();
    const { sub = '' } = useParams();
    const config = useRuntimeConfig();
    const { authenticated, accessToken, reauthenticate } = useAuth();
    const can = useCan();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const toast = useToast();
    const [searchParams, setSearchParams] = useSearchParams();

    const canView =
        can('console.accounts.view') ||
        can('console.accounts.issue') ||
        can('console.accounts.reset') ||
        can('console.accounts.delete');
    const canDelete = can('console.accounts.delete');
    const canReset = can('console.accounts.reset');
    const canLock = can('console.accounts.lock');
    const canViewAudit = can('console.audit.view');

    const rawTab = searchParams.get('tab');
    const tab: AccountProfileTabKey =
        rawTab === 'permissions' || rawTab === 'audit' ? rawTab : 'overview';

    const setTab = useCallback(
        (next: AccountProfileTabKey) => {
            setSearchParams((prev) => {
                const params = new URLSearchParams(prev);
                if (next === 'overview') params.delete('tab');
                else params.set('tab', next);
                return params;
            });
        },
        [setSearchParams]
    );

    const enabled = authenticated && Boolean(accessToken) && Boolean(sub) && canView;

    const detailQuery = useQuery({
        queryKey: ['account-detail', sub, accessToken],
        queryFn: () => fetchAccount(config.apiBase, accessToken as string, sub),
        enabled,
        retry: false,
    });

    const auditPreviewQuery = useQuery({
        queryKey: ['account-audit-preview', sub, detailQuery.data?.username, accessToken],
        queryFn: () =>
            fetchAccountAudit(config.apiBase, accessToken as string, {
                page: 1,
                username: detailQuery.data?.username ?? '',
            }),
        enabled: enabled && canViewAudit && Boolean(detailQuery.data?.username),
        retry: false,
    });

    const auditCountQuery = useQuery({
        queryKey: ['account-audit-count', detailQuery.data?.username, accessToken],
        queryFn: () =>
            fetchAccountAudit(config.apiBase, accessToken as string, {
                page: 1,
                username: detailQuery.data?.username ?? '',
            }),
        enabled: enabled && canViewAudit && Boolean(detailQuery.data?.username),
        retry: false,
    });

    const invalidate = useCallback(() => {
        void queryClient.invalidateQueries({ queryKey: ['account-detail', sub] });
        void queryClient.invalidateQueries({ queryKey: ['accounts'] });
        void queryClient.invalidateQueries({ queryKey: ['account-audit-preview', sub] });
        void queryClient.invalidateQueries({ queryKey: ['account-audit-count'] });
    }, [queryClient, sub]);

    const [resetOpen, setResetOpen] = useState(false);
    const [resetIdent, setResetIdent] = useState('');
    const [resetResult, setResetResult] = useState<AccountPasswordReset | null>(null);
    const [resetError, setResetError] = useState<string | null>(null);

    // EAA-1779: same credential choice as the accounts board's drawer — this
    // page renders the identical `ResetAccountDrawer`, so it must offer the
    // identical modes rather than silently staying temp-password-only.
    const resetCredential = useResetCredential();

    const resetMutation = useMutation({
        mutationFn: ({ endSessions }: { endSessions: boolean }) =>
            resetAccountPassword(config.apiBase, accessToken as string, {
                sub,
                end_sessions: endSessions,
                ...resetCredential.toRequest(),
            }),
        onSuccess: (data) => {
            setResetOpen(false);
            // Admin-set mode reveals nothing — see AccountsPage for the reasoning.
            setResetResult(data.mode === 'admin_set' ? null : data);
            resetCredential.clear();
            setResetError(null);
            toast.success(t('console.accounts.toasts.resetSuccess', { username: data.username }));
            invalidate();
        },
        onError: (error) => {
            setResetError(
                error instanceof OrgAdminApiError ? error.message : t('console.accounts.toasts.resetErrorFallback')
            );
        },
    });

    const [lockTarget, setLockTarget] = useState<KeycloakAccountRow | null>(null);
    const [lockError, setLockError] = useState<string | null>(null);

    const lockMutation = useMutation({
        mutationFn: ({ sub: accountSub, endSessions }: { sub: string; username: string; endSessions: boolean }) =>
            updateAccountEnabled(config.apiBase, accessToken as string, accountSub, false, { endSessions }),
        onSuccess: (_data, { username }) => {
            setLockTarget(null);
            setLockError(null);
            toast.success(t('console.accounts.lockModal.success', { username }));
            invalidate();
        },
        onError: (error) => {
            setLockError(
                error instanceof OrgAdminApiError ? error.message : t('console.accounts.deactivate.errorFallback')
            );
        },
    });

    const [deleteTarget, setDeleteTarget] = useState<KeycloakAccountRow | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    const deleteMutation = useMutation({
        mutationFn: (accountSub: string) => deleteAccount(config.apiBase, accessToken as string, accountSub),
        onSuccess: () => {
            toast.success(t('console.accounts.toasts.deleteSuccess', { sub }));
            navigate('/console/accounts');
        },
        onError: (error) => {
            setDeleteError(
                error instanceof OrgAdminApiError ? error.message : t('console.accounts.toasts.deleteErrorFallback')
            );
        },
    });

    const detail = detailQuery.data;
    const displayName = detail ? accountDisplayName(detail) : '';

    const rowForDelete = useMemo((): KeycloakAccountRow | null => {
        if (!deleteTarget) return null;
        if (!detail || deleteTarget.sub !== detail.sub) return deleteTarget;
        return {
            ...detail,
            placement:
                detail.placements.length > 0
                    ? {
                          organization_id: detail.placements[0].organization_id,
                          organization_name: detail.placements[0].organization_name,
                          level_name: detail.placements[0].level_name,
                          count: detail.placements.length,
                      }
                    : null,
        };
    }, [deleteTarget, detail]);

    const recentAudit = useMemo(
        () => auditPreviewQuery.data?.results.slice(0, 5).map(toUnifiedFromAccountAudit) ?? [],
        [auditPreviewQuery.data]
    );

    const openReset = useCallback(() => {
        setResetIdent(sub);
        setResetError(null);
        setResetOpen(true);
    }, [sub]);

    const openLock = useCallback(() => {
        if (!detail) return;
        setLockTarget(detail);
        setLockError(null);
    }, [detail]);

    const openDelete = useCallback(() => {
        if (!detail) return;
        setDeleteTarget({
            ...detail,
            placement:
                detail.placements.length > 0
                    ? {
                          organization_id: detail.placements[0].organization_id,
                          organization_name: detail.placements[0].organization_name,
                          level_name: detail.placements[0].level_name,
                          count: detail.placements.length,
                      }
                    : null,
        });
        setDeleteError(null);
    }, [detail]);

    // EAA-1825: loop-guarded — see AuthProvider.reauthenticate / signInLoop.ts.
    if (detailQuery.error instanceof OrgAdminUnauthorizedError) {
        reauthenticate(`/console/accounts/${encodeURIComponent(sub)}`);
    }

    return {
        sub,
        tab,
        setTab,
        canView,
        canDelete,
        canReset,
        canLock,
        canViewAudit,
        detailQuery,
        detail,
        displayName,
        recentAudit,
        auditCount: auditCountQuery.data?.count,
        resetOpen,
        setResetOpen,
        resetIdent,
        resetCredential,
        setResetIdent,
        resetResult,
        setResetResult,
        resetError,
        resetMutation,
        openReset,
        lockTarget,
        lockError,
        setLockTarget,
        lockMutation,
        openLock,
        deleteTarget: rowForDelete,
        deleteError,
        setDeleteTarget,
        deleteMutation,
        openDelete,
        navigate,
    };
}

export default useAccountProfilePage;
